import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { Alert, Button, Input, Surface, styles } from "../../tmwx-design-web/src";
import { useTranslation } from "react-i18next";
import { checkFieldError, isEmpty } from "../../gui-common/appHelper";
import { RootState } from "../../gui-common/redux/store";
import { ResponseBase } from "../../gui-common/redux/data";
import { setSnackbarData } from "../../gui-common/redux/action/snackbarAction";
import { AddMachineCategoryRequest, EditMachineCategoryRequest, MachineCategory, initialAddMachineCategoryRequest, isAddMachineCategoryRequestValid, isEditMachineCategoryRequestValid } from "../../gui-common/redux/data/machineCategory";
import { addNewATRMachineCategory, clearATRMachineCategoryError, clearATRMachineCategoryResponse, editATRMachineCategory, getATRMachineCategoryList } from "../../gui-common/redux/action/atr/machineCategoryAction";
import { getFieldErrorText } from "../../utils/helper";

interface HandleMachineCategoryProps {
    actionType: 'edit' | 'add'
    item?: MachineCategory | null
    onClose?: () => void
}

const HandleMachineCategory: React.FC<HandleMachineCategoryProps> = ({ actionType, item, onClose = () => { } }) => {
    const { t } = useTranslation()
    const dispatch: any = useDispatch();
    const serverError = useSelector<RootState, ResponseBase | null>(state => state.atrMachineCategory.error);
    const response = useSelector<RootState, ResponseBase | null>(state => state.atrMachineCategory.response);
    const [validationMessage, setValidationMessage] = useState<string>("");
    const [request, setRequest] = useState<AddMachineCategoryRequest>(initialAddMachineCategoryRequest);

    const handleClear = () => {
        setRequest(initialAddMachineCategoryRequest)
        dispatch(clearATRMachineCategoryResponse())
        dispatch(clearATRMachineCategoryError())
        onClose()
    }

    const handleChangeName = (text: string) => {
        if (serverError) {
            dispatch(clearATRMachineCategoryError())
        }

        setRequest({ ...request, name: text })
    }

    const handleChangeDescription = (text: string) => {
        if (serverError) {
            dispatch(clearATRMachineCategoryError())
        }

        setRequest({ ...request, description: text })
    }

    const handleMachineCategoryClick = () => {
        if (item && actionType === 'edit') {
            ///modify
            setValidationMessage('')

            let editRequest: EditMachineCategoryRequest = {
                id: item.id,
                name: request.name,
                description: request.description
            }

            if (isEditMachineCategoryRequestValid(editRequest)) {
                dispatch(editATRMachineCategory(editRequest));
            } else {
                setValidationMessage(t('mandatoryFields'));
            }
        } else {
            //save
            setValidationMessage('')
            if (isAddMachineCategoryRequestValid(request as AddMachineCategoryRequest)) {
                dispatch(addNewATRMachineCategory(request as AddMachineCategoryRequest));
            } else {
                setValidationMessage(t('mandatoryFields'));
            }
        }
    }

    /* HANDLE EDIT REQUEST VALUES */
    useEffect(() => {
        if (item) {
            let obj: AddMachineCategoryRequest = {
                name: item.name ? item.name : '',
                description: item.description ? item.description : ''
            }

            setRequest(obj)
        }
    }, [item])

    /* HANDLE RESPONSE */
    useEffect(() => {
        if (response) {
            if (response.responseCode === 'SUCCESS') {
                handleClear()
                dispatch(setSnackbarData({
                    status: 'success',
                    text: t('actionSuccess')
                }))
                dispatch(getATRMachineCategoryList());
            }
        }
    }, [response])

    return (
        <Surface elevation={0}>
            {serverError && (
                <Alert style={{ marginBottom: 8 }} status="danger" text={t('errorOccurred')} />
            )}

            {!isEmpty(validationMessage) && (
                <Alert style={{ marginBottom: 8 }} text={validationMessage} status="danger" />
            )}

            <Input
                fullWidth
                error={checkFieldError('name', serverError)}
                icon={"badge_outlined"}
                placeholder={t('name')}
                value={request.name}
                onChangeValue={(text) => handleChangeName(text)}
                helperText={getFieldErrorText('name', serverError, t)}
            />

            <Input
                fullWidth
                error={checkFieldError('description', serverError)}
                icon={"description_outlined"}
                placeholder={t('desc')}
                value={request.description}
                multiline
                numberOfLines={4}
                onChangeValue={(text) => handleChangeDescription(text)}
                helperText={getFieldErrorText('description', serverError, t)}
            />

            <div style={{ ...styles.marginTop8, ...styles.row, ...{ justifyContent: 'flex-end' } }}>
                <Button status="danger" variant="outlined" onPress={handleClear}>
                    {t('cancel')}
                </Button>
                <Button style={styles.marginLeft16} onPress={handleMachineCategoryClick}>
                    {actionType === 'add' ? t('add') : t('modify')}
                </Button>
            </div>
        </Surface>
    )
};

export default HandleMachineCategory;
